import React, { useState, useEffect, useCallback } from "react";
import { docentesDisponibles, importarDesdeDocentes } from "../../../../services/empleadosService";
import { showToast } from "../../../../utils/showToast";

const TIPOS_CONTRATACION = [
  { value: "servicios", label: "Servicios (factura)" },
  { value: "nomina",    label: "Nómina" },
  { value: "hibrido",   label: "Híbrido" },
];

export default function ImportarDocentesModal({ open, onClose, onImportado }) {
  const [docentes, setDocentes]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [seleccionados, setSeleccionados] = useState({});
  const [contratacion, setContratacion]   = useState({});

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await docentesDisponibles();
      setDocentes(Array.isArray(data) ? data : []);
      // Inicializar contratacion en "servicios" por defecto
      const init = {};
      (Array.isArray(data) ? data : []).forEach(d => { init[d.id] = "servicios"; });
      setContratacion(init);
      setSeleccionados({});
    } catch {
      showToast({ type: "danger", message: "No se pudo cargar la lista de docentes." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) cargar(); }, [open, cargar]);

  const toggleSeleccion = (id) =>
    setSeleccionados(prev => ({ ...prev, [id]: !prev[id] }));

  const todosSeleccionados = docentes.length > 0 && docentes.every(d => seleccionados[d.id]);

  const toggleTodos = () => {
    const nuevoEstado = !todosSeleccionados;
    const nuevo = {};
    docentes.forEach(d => { nuevo[d.id] = nuevoEstado; });
    setSeleccionados(nuevo);
  };

  const cantidadSeleccionada = docentes.filter(d => seleccionados[d.id]).length;

  const handleImportar = async () => {
    const lista = docentes
      .filter(d => seleccionados[d.id])
      .map(d => ({
        docente_id:        d.id,
        nombres:           d.nombres,
        apellidos:         d.apellidos,
        documento:         d.documento,
        email:             d.email,
        telefono:          d.telefono,
        tipo_contratacion: contratacion[d.id] || "servicios",
      }));

    if (!lista.length) {
      showToast({ type: "warning", message: "Selecciona al menos un docente." });
      return;
    }

    setSaving(true);
    try {
      const res = await importarDesdeDocentes(lista);
      showToast({ type: "success", message: res.message });
      onImportado?.();
      onClose();
    } catch (err) {
      showToast({ type: "danger", message: err.message || "Error al importar docentes." });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="modal fade show d-block management-modal"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(15,23,42,0.45)" }}
      aria-modal="true"
      role="dialog"
    >
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
        <div className="modal-content">
          <div className="modal-header border-0">
            <div>
              <h5 className="modal-title mb-0">Importar docentes como empleados</h5>
              <small className="text-muted">
                Solo aparecen docentes con cédula registrada que aún no están en RRHH.
              </small>
            </div>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body pt-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status" />
              </div>
            ) : docentes.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-check-circle fs-2 d-block mb-2 text-success" />
                Todos los docentes ya están registrados como empleados.
              </div>
            ) : (
              <table className="table align-middle management-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={todosSeleccionados}
                        onChange={toggleTodos}
                        title="Seleccionar todos"
                      />
                    </th>
                    <th>Docente</th>
                    <th>Cédula</th>
                    <th>RUC generado</th>
                    <th>Email</th>
                    <th>Contratación</th>
                  </tr>
                </thead>
                <tbody>
                  {docentes.map(d => {
                    const tipo = contratacion[d.id] || "servicios";
                    const esServicios = tipo === "servicios" || tipo === "hibrido";
                    const rucGenerado = esServicios && d.documento?.length === 10
                      ? `${d.documento}001`
                      : esServicios ? d.documento : "—";

                    return (
                      <tr
                        key={d.id}
                        className={seleccionados[d.id] ? "table-primary" : ""}
                        onClick={() => toggleSeleccion(d.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <td onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={!!seleccionados[d.id]}
                            onChange={() => toggleSeleccion(d.id)}
                          />
                        </td>
                        <td>
                          <div className="fw-semibold">{d.apellidos}, {d.nombres}</div>
                        </td>
                        <td>{d.documento}</td>
                        <td>
                          <small className={esServicios ? "text-success" : "text-muted"}>
                            {rucGenerado}
                          </small>
                        </td>
                        <td>{d.email || <span className="text-muted">—</span>}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <select
                            className="form-select form-select-sm"
                            style={{ minWidth: 160 }}
                            value={tipo}
                            onChange={e => setContratacion(prev => ({ ...prev, [d.id]: e.target.value }))}
                          >
                            {TIPOS_CONTRATACION.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="modal-footer border-0">
            <span className="text-muted me-auto">
              {cantidadSeleccionada > 0
                ? `${cantidadSeleccionada} seleccionado(s)`
                : "Ninguno seleccionado"}
            </span>
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn management-btn-primary"
              onClick={handleImportar}
              disabled={saving || cantidadSeleccionada === 0}
            >
              {saving
                ? <><span className="spinner-border spinner-border-sm me-2" />Importando...</>
                : <><i className="bi bi-download me-2" />Importar {cantidadSeleccionada > 0 ? cantidadSeleccionada : ""}</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
